import * as React from "react";
import { Label, Pie, PieChart } from "recharts";
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  ChartConfig,
  ChartContainer,
  ChartTooltip,
  ChartTooltipContent,
} from "@/components/ui/chart";
import { formatNumber, toLocaleString } from "@/lib/utils";
import { useListings } from "@/hooks/use-listings";
import { WithErrorAndLoading } from "@/components/with-error-and-loading";

type ChartData = { lst: string; staked: number; fill: string };

export function DistributionPie() {
  const { isPending: loading, error, data: listings } = useListings();

  const totalDualSTAKE = listings?.size;

  const [chartConfig, chartData, total] = React.useMemo(() => {
    if (!listings?.size) return [{ staked: { label: "Staked" } }, [], 0];
    return [...listings.values()].reduce(
      ([config, data, total], { lstName, staked }, idx) => {
        config[lstName] = { label: lstName, color: "hsl(var(--chart-1))" };
        const colorIdx = (idx % 5) + 1;
        data.push({
          lst: lstName,
          staked: Number(staked) / 1e6,
          fill: `hsl(var(--chart-${colorIdx}))`,
        });
        total += Number(staked);
        return [config, data, total];
      },
      [{ staked: { label: "Staked" } }, [], 0] as [
        ChartConfig,
        ChartData[],
        number
      ]
    );
  }, [listings]);

  return (
    <WithErrorAndLoading loading={loading} error={error}>
      <Card className="flex flex-col">
        <CardHeader className="items-center pb-0">
          <CardTitle>dualSTAKE Distribution</CardTitle>
          <CardDescription>ALGO staked across dualSTAKE tokens</CardDescription>
        </CardHeader>
        <CardContent className="flex-1 pb-0">
          <ChartContainer
            config={chartConfig}
            className="mx-auto aspect-square max-h-[250px]"
          >
            <PieChart>
              <ChartTooltip
                cursor={false}
                content={<ChartTooltipContent hideLabel />}
              />
              <Pie
                data={chartData}
                dataKey="staked"
                nameKey="lst"
                innerRadius={60}
                strokeWidth={5}
              >
                <Label
                  content={({ viewBox }) => {
                    if (viewBox && "cx" in viewBox && "cy" in viewBox) {
                      return (
                        <text
                          x={viewBox.cx}
                          y={viewBox.cy}
                          textAnchor="middle"
                          dominantBaseline="middle"
                        >
                          <tspan
                            x={viewBox.cx}
                            y={viewBox.cy}
                            className="fill-foreground text-3xl font-bold"
                          >
                            {toLocaleString(totalDualSTAKE ?? 0)}
                          </tspan>
                          <tspan
                            x={viewBox.cx}
                            y={(viewBox.cy || 0) + 24}
                            className="fill-muted-foreground"
                          >
                            Tokens
                          </tspan>
                        </text>
                      );
                    }
                  }}
                />
              </Pie>
            </PieChart>
          </ChartContainer>
        </CardContent>
        <CardFooter className="flex-col gap-2 text-sm">
          <div className="flex items-center gap-2 font-medium leading-none">
            {formatNumber(total, 6, 0)} ALGO staked
          </div>
        </CardFooter>
      </Card>
    </WithErrorAndLoading>
  );
}
